import { Box, Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ClientBankDTO,
  deleteClientBank,
  getClientBank,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsDrawer,
  WsIconText,
  WsIconEdit,
  WsIconMoreVer,
  WsListingLoader,
  WsDataGrid,
} from 'wisestep-base';

import { ConfirmDeleteDialog } from '../../../../components';
import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext/WorkspaceContext';
import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import PopoverOnClick from '../../../../features/clientListingPage/components/PopoverOnClick';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import ClientBankInfo from '../../../clientListingPage/components/ClientBankInfo';
import { StyledGrid } from '../../../clientListingPage/components/ClientList';
import { getDefaultClientBank } from '../../../clientListingPage/helper/ClientListingHelper';
import IClientBank from '../../../clientListingPage/types/IClientBank';
import { IClientDetailsTabs, clientsAmplitudeEvents } from '../../helper/clientDetailsHelper';
import AddEntityButton from '../addEntityButton/AddEntityButton';
import { ILegalState, initialState } from '../legalIdentifiersTab/LegalIdentifiersTab';

const BankDetailsTab = ({ clientId }: IClientDetailsTabs) => {
  const [banks, setBanks] = useState<ClientBankDTO[]>([]);
  const tableData: Record<string, unknown>[] = [];
  const [openDialogs, setOpenDialogs] = useState<ILegalState>(initialState);
  const [selectedBank, setSelectedBank] = useState<IClientBank>(getDefaultClientBank(clientId));
  const [loader, setLoader] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspace = useWorkspaceContext();

  const isAuthorizedAddBankingInformation = useClientAuthz(
    ClientAction.ADD_VIEW_BANKING_INFORMATION
  );
  const isAuthorizedEditBankingInformation = useClientAuthz(
    ClientAction.EDIT_VIEW_BANKING_INFORMATION
  );
  const isAuthorizedDeleteBankingInformation = useClientAuthz(
    ClientAction.DELETE_VIEW_BANKING_INFORMATION
  );

  const fetchData = useCallback(async () => {
    try {
      setLoader(true);
      const response = await getClientBank(clientId);
      setBanks(response.data.data);
      setLoader(false);
    } catch {
      setNotification(failed(`Failed to fetch Banks list`));
    }
  }, [clientId, setNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'BankName',
      headerName: 'Bank Name',
      flex: 1,
    },
    {
      field: 'branchName',
      headerName: 'Branch Name',
      flex: 1,
    },
    {
      field: 'bankCode',
      headerName: 'Bank Code',
      flex: 1,
    },
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      flex: 1,
    },
    {
      field: 'swiftCode',
      headerName: 'Swift Code',
      flex: 1,
    },
    {
      field: 'iban',
      headerName: 'IBAN',
      flex: 1,
    },
    {
      field: 'id',
      headerName: 'id',
      hide: true,
      flex: 1,
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <Box p={2}>
            {isAuthorizedEditBankingInformation && (
              <WsIconText
                text="Edit"
                textCursor="pointer"
                icon={<WsIconEdit />}
                onClick={() => onEditBank(params.row)}
              />
            )}
          </Box>
        </PopoverOnClick>
      ),
    },
  ];

  banks?.map((bank) => {
    tableData.push({
      id: bank?.id,
      BankName: bank.bankName,
      country: bank?.country,
      branchName: bank.branchName,
      bankCode: bank.bankCode,
      accountNumber: bank.bankAccountNumber,
      swiftCode: bank.swiftCode,
      iban: bank.iban,
    });
  });

  const handleCloseDialog = () => {
    fetchData();
    setOpenDialogs(initialState);
  };

  const onAddButtonClick = () => {
    clientsAmplitudeEvents(
      'client_details_bank_details_add',
      'Client Details Bank Details Add',
      workspace
    );
    setOpenDialogs({
      ...openDialogs,
      editFlag: false,
      openAddOrEditDialog: true,
    });
  };

  const onEditBank = (bankInfo: ClientBankDTO) => {
    clientsAmplitudeEvents(
      'client_details_bank_details_edit',
      'Client Details Bank Details Edit',
      workspace
    );
    setOpenDialogs({ ...openDialogs, editFlag: true, openAddOrEditDialog: true });
    fetchAndSetDataByIndex(bankInfo);
  };

  const fetchAndSetDataByIndex = (bankInfo: ClientBankDTO) => {
    if (bankInfo) {
      const legalIdentifierDataObj: IClientBank = {
        data: {
          ...bankInfo,
          clientId: clientId,
        },
        isEditable: true,
      };
      setSelectedBank(legalIdentifierDataObj);
    } else
      setNotification(
        failed('Failed to fetch selected legal identifiers details, Please try again !!')
      );
  };

  const deleteBank = async () => {
    try {
      const avPayload = { ...activityLogDTOHelper };
      if (selectedBank.data && selectedBank.data.id) {
        await deleteClientBank(selectedBank.data.id);
        avPayload.action = LogAction.Delete;
        avPayload.description = `deleted ${selectedBank.data.bankName} in Bank Details for`;
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.entityType = LogEntityType.Bank;
        avPayload.parentEntitiyId = selectedBank.data.clientId;
        avPayload.entityId = selectedBank.data.id;
        avPayload.field = `Bank Details`;
        avPayload.entityName = selectedBank.data.bankName;
        await saveActivityLog(avPayload);
        setNotification(
          success(`${selectedBank?.data?.bankName} Bank has been deleted successfully !!`)
        );
        handleCloseDialog();
      }
    } catch {
      setNotification(failed('Something went wrong, Please try again'));
    }
  };

  return (
    <>
      <StyledGrid item xs={12} mt={2}>
        {isAuthorizedAddBankingInformation && (
          <AddEntityButton
            onClick={onAddButtonClick}
            justifyContent="flex-end"
            buttonText="Add Bank details"
          />
        )}
        {banks?.length > 0 && !loader ? (
          <Grid>
            {isAuthorizedEditBankingInformation || isAuthorizedDeleteBankingInformation ? (
              <WsDataGrid
                autoHeight
                columns={columns}
                rows={tableData}
                paginationMode="server"
                sortingMode="server"
              />
            ) : (
              <WsListingLoader />
            )}
          </Grid>
        ) : (
          <Box
            sx={{
              '& .MuiCard-root': {
                boxShadow: 'none',
                elevation: 0,
              },
            }}
          >
            {loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard
                text={
                  isAuthorizedAddBankingInformation
                    ? 'Click on +Add bank Details to add Banking Information'
                    : 'No Bank Details available for the client'
                }
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={
                  isAuthorizedAddBankingInformation ? (
                    <AddEntityButton
                      buttonText="Add Bank details"
                      onClick={onAddButtonClick}
                      justifyContent="center"
                    />
                  ) : (
                    <></>
                  )
                }
              />
            )}
          </Box>
        )}
      </StyledGrid>
      {openDialogs.openAddOrEditDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsDrawer
            open={openDialogs.openAddOrEditDialog}
            onClose={handleCloseDialog}
            title="Add Bank Details"
          >
            <Box m={2}>
              <ClientBankInfo
                clientId={clientId}
                listPage={true}
                loadTabData={fetchData}
                showModal={() => setOpenDialogs({ ...openDialogs, openAddOrEditDialog: false })}
                editObj={selectedBank}
                editFlag={openDialogs.editFlag}
              />
            </Box>
          </WsDrawer>
        </ErrorBoundary>
      )}
      {openDialogs.openDeleteDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <ConfirmDeleteDialog
            open={openDialogs.openDeleteDialog}
            onClose={handleCloseDialog}
            onConfirm={deleteBank}
            deletingItem={`${selectedBank?.data?.bankName ?? ''} bank`}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

export default BankDetailsTab;
